import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useOutsideClick } from '../../../../core/react/hooks/index.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { Animated } from '../../components/commonComponents/Animated.js';

interface OverlayProps {
  children: React.ReactNode;
  title?: string;
  handleOverlay?: () => void;
  className?: string;
}

export const Overlay: React.FC<OverlayProps> = ({
  children,
  title,
  handleOverlay,
  className,
}) => {
  const { trackClickUIElement } = usePulseContext();
  const [show, setShow] = useState(false);
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.getElementById('rootComponent');

    if (!root) {
      return;
    }

    setMountNode(root);
  }, []);

  useEffect(() => {
    setShow(true);
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    setTimeout(() => {
      handleOverlay?.();
    }, 200);
  }, [handleOverlay]);

  const handleClick = (): void => {
    trackClickUIElement(
      {
        object: {
          id: 'modal:close-button',
          name: 'Close modal button',
          elementType: 'Button',
        },
      },
      {
        object: {
          objectId: 'modal:close-button',
          name: 'Close modal button',
          elementType: 'Button',
        },
        placement: { containerName: 'page' },
      },
    );

    handleClose();
  };

  const containerRef = useOutsideClick(handleClose);

  const classNames = ['overlay', className].filter(Boolean).join(' ');

  const content = (
    <div
      className={classNames}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <Animated show={show}>
        <div className="modal" ref={containerRef}>
          {title && handleOverlay && (
            <div className="modal-header">
              <button
                className="modal-close"
                onClick={handleClick}
                aria-label="Lukk"
              >
                Lukk
              </button>
              <div className="modal-title">{title}</div>
            </div>
          )}
          {children}
        </div>
      </Animated>
    </div>
  );

  if (!mountNode) {
    return null;
  }

  return createPortal(content, mountNode);
};
